import * as React from 'react';

import { translate } from '~l10n/client';

import { IconButton, SmallButton } from '~shared/components/buttons';
import { IconRestore } from '~shared/components/icons/standard';
import TextInput from '~shared/components/TextInput';
import { TooltipV2 } from '~shared/components/TooltipV2';

import { sleep } from '../../../util/sleep';

const COPYING_MESSAGE_LENGTH = 1500;

interface CopyResettableLinkProps {
  isCopyEnabled?: boolean;
  link: string;
  linkTextboxLabel: string;
  resetLinkLabel: string;
  resetLinkDisabled: boolean;
  handleResetLink: () => void;
}

export function CopyResettableLink({
  isCopyEnabled = true,
  link,
  linkTextboxLabel,
  resetLinkLabel,
  resetLinkDisabled,
  handleResetLink,
}: CopyResettableLinkProps) {
  const [isCopying, setIsCopying] = React.useState(false);
  const [inputRef, setInputRef] = React.useState<HTMLInputElement>(null);

  const handleCopy = () => {
    if (!isCopyEnabled) return;

    // set temporary state
    setIsCopying(true);

    // Select all the text
    inputRef.select();

    // use the clipboard api
    navigator.clipboard
      .writeText(link)
      .then(() => sleep(COPYING_MESSAGE_LENGTH))
      .then(() => setIsCopying(false));
  };

  return (
    <div className="flex flex-row gap-4">
      <div className="flex-grow">
        <TextInput
          readOnly
          value={link}
          aria-label={linkTextboxLabel}
          ref={(ref) => setInputRef(ref)}
        />
      </div>
      <div className="flex flex-col justify-end">
        <SmallButton
          className="whitespace-nowrap min-w-22"
          onClick={handleCopy}
        >
          {isCopying ? translate('Copied') : translate('Copy Link')}
        </SmallButton>
      </div>
      <div className="flex flex-col justify-end">
        <TooltipV2 label={resetLinkLabel}>
          <IconButton
            aria-label={resetLinkLabel}
            disabled={resetLinkDisabled}
            onClick={handleResetLink}
          >
            <IconRestore />
          </IconButton>
        </TooltipV2>
      </div>
    </div>
  );
}
